import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ScenarioDetail, ScenarioDetailProcedure } from '@/lib/scenarioDetailStore';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';

// Code type → route prefix
const CODE_TYPE_ROUTE: Record<string, string> = {
  CPT: '/cpt', HCPCS: '/hcpcs', 'MS-DRG': '/drg', DRG: '/drg', REV: '/revenue',
};

// Code type → analytics procedure_type
const CODE_TYPE_MAP: Record<string, string> = {
  CPT: 'cpt', HCPCS: 'hcpcs', DRG: 'drg', 'MS-DRG': 'drg', REV: 'revenue', NDC: 'ndc', DRUG: 'ndc',
};

// Code type badge colors
const CODE_TYPE_COLORS: Record<string, string> = {
  CPT: '#3b82f6', HCPCS: '#8b5cf6', 'MS-DRG': '#f59e0b', DRG: '#f59e0b',
  REV: '#10b981', NDC: '#ec4899', DRUG: '#ec4899', LOCAL: '#6b7280',
};

interface ScenarioCoverageModalProps {
  isOpen: boolean;
  onClose: () => void;
  hospitalId: string | null;
  hospitalName: string;
  scenarioDetail: ScenarioDetail | null | undefined;
  scenarioName: string;
  coveragePct: number | null;
  matchedCodes: number | null;
  totalCodes: number | null;
  hospitalTotalPrice: number | null;
  nationalTotalPrice: number | null;
}

interface CodeRow {
  code: string;
  codeType: string;
  description: string;
  quantity: number;
  setting: string;
  nationalAvg: number | null;
  nationalMedian: number | null;
  hospitalPrice: number | null;
  markup: number | null;
}

export function ScenarioCoverageModal({
  isOpen, onClose, hospitalId, hospitalName, scenarioDetail,
  scenarioName, coveragePct, matchedCodes, totalCodes,
  hospitalTotalPrice, nationalTotalPrice,
}: ScenarioCoverageModalProps) {
  const { t } = useTranslation(['rankings', 'common']);
  const [codeRows, setCodeRows] = useState<CodeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const brand = useBrand();
  const { isDark: themeIsDark } = useTheme();
  const isAhp = brand.design === 'civic';
  const isDark = isAhp ? themeIsDark : true;
  // Theme tokens
  const bg = isDark ? '#0a0f1a' : '#ffffff';
  const border = isDark ? 'rgba(var(--civic-gold-rgb),0.3)' : '#e2e0d8';
  const text = isDark ? '#e5e7eb' : 'var(--civic-ink)';
  const textMuted = isDark ? 'rgba(229,231,235,0.5)' : '#6b7280';
  const textFaint = isDark ? 'rgba(229,231,235,0.4)' : '#9ca3af';
  const rowBorder = isDark ? 'rgba(229,231,235,0.06)' : 'rgba(0,0,0,0.06)';
  const summaryBg = isDark ? 'rgba(var(--civic-gold-rgb),0.08)' : 'rgba(var(--civic-gold-rgb),0.06)';
  const divider = isDark ? 'rgba(229,231,235,0.15)' : 'rgba(0,0,0,0.1)';

  useEffect(() => {
    if (isOpen && hospitalId && scenarioDetail?.procedures?.length) {
      fetchHospitalPrices();
    }
  }, [isOpen, hospitalId, scenarioDetail?.id]);

  const fetchHospitalPrices = async () => {
    if (!hospitalId || !scenarioDetail?.procedures?.length) return;

    setLoading(true);
    try {
      const procs = scenarioDetail.procedures;
      const np = scenarioDetail.nationalPricing || {};

      // Collect unique codes to query
      const codes = procs.map(p => p.code);

      // Query hospital_procedure_analytics for this hospital + these codes
      const { data, error } = await supabase
        .from('hospital_procedure_analytics')
        .select('procedure_code, procedure_type, hospital_gross_charge, procedure_description')
        .eq('hospital_id', hospitalId)
        .in('procedure_code', codes);

      if (error) {
        console.warn('[CoverageModal] Query error:', error);
      }

      // Build lookup: code → hospital price (use max if multiple)
      const hospitalPrices: Record<string, { price: number; desc: string }> = {};
      if (data) {
        for (const row of data) {
          const price = Number(row.hospital_gross_charge);
          if (price > 0) {
            const existing = hospitalPrices[row.procedure_code];
            if (!existing || price > existing.price) {
              hospitalPrices[row.procedure_code] = {
                price,
                desc: row.procedure_description || '',
              };
            }
          }
        }
      }

      // Build rows from scenario procedures
      const rows: CodeRow[] = procs.map(proc => {
        const hospitalData = hospitalPrices[proc.code];
        const hospitalPrice = hospitalData?.price ?? null;

        // Look up national pricing (try exact key variations)
        let natAvg: number | null = null;
        let natMedian: number | null = null;
        const settingFull = proc.setting || '';  // "inpatient", "outpatient", or ""
        const settingShort = settingFull === 'outpatient' ? 'OP' : settingFull === 'inpatient' ? 'IP' : '';

        // Try exact match with full setting name, then short, then unspecified, then all_settings
        for (const suffix of [settingFull, settingShort, '', 'all_settings']) {
          const key = `${proc.codeType}:${proc.code}:${suffix}`;
          const entry = np[key];
          if (entry && entry.p > 0) {
            natAvg = entry.p;
            natMedian = entry.m ?? null;
            break;
          }
        }

        // Compute per-unit markup
        let markup: number | null = null;
        if (hospitalPrice != null && natAvg != null && natAvg > 0) {
          markup = ((hospitalPrice - natAvg) / natAvg) * 100;
        }

        return {
          code: proc.code,
          codeType: proc.codeType,
          description: proc.description,
          quantity: proc.quantity || 1,
          setting: proc.setting || '',
          nationalAvg: natAvg,
          nationalMedian: natMedian,
          hospitalPrice,
          markup,
        };
      });

      // Sort: matched (has hospital price) first, then by absolute markup desc
      rows.sort((a, b) => {
        const aHas = a.hospitalPrice != null ? 0 : 1;
        const bHas = b.hospitalPrice != null ? 0 : 1;
        if (aHas !== bHas) return aHas - bHas;
        const aMarkup = Math.abs(a.markup ?? 0);
        const bMarkup = Math.abs(b.markup ?? 0);
        return bMarkup - aMarkup;
      });

      setCodeRows(rows);
    } catch (e) {
      console.warn('[CoverageModal] Error:', e);
    } finally {
      setLoading(false);
    }
  };

  const fmtPrice = (v: number | null) => {
    if (v == null) return '—';
    if (v >= 10000) return `$${(v / 1000).toFixed(1)}k`;
    return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const matchedCount = codeRows.filter(r => r.hospitalPrice != null).length;
  const totalHospital = codeRows.reduce((s, r) =>
    s + (r.hospitalPrice != null ? r.hospitalPrice * r.quantity : 0), 0);
  const totalNational = codeRows.reduce((s, r) =>
    s + (r.nationalAvg != null ? r.nationalAvg * r.quantity : 0), 0);

  return (
    <Dialog open={isOpen} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" style={{
        backgroundColor: bg,
        borderColor: border,
        color: text,
      }}>
        <DialogHeader>
          <DialogTitle className="text-base font-semibold" style={{ color: 'var(--civic-gold)' }}>
            {t('coverageModal.title')}
          </DialogTitle>
          <DialogDescription className="text-sm" style={{ color: textMuted }}>
            {t('coverageModal.hospitalDash', { hospital: hospitalName, scenario: scenarioName })}
          </DialogDescription>
        </DialogHeader>

        {/* Summary bar */}
        <div className="flex items-center gap-3 text-xs font-mono py-2 px-3 rounded" style={{
          backgroundColor: summaryBg,
          borderLeft: '3px solid var(--civic-gold)',
        }}>
          <div>
            <span style={{ color: textMuted }}>{t('coverageModal.coverage')}</span>{' '}
            <span className="font-bold" style={{ color: coveragePct != null && coveragePct >= 80 ? '#4ade80' : '#fbbf24' }}>
              {coveragePct != null ? `${coveragePct.toFixed(0)}%` : '—'}
            </span>
            <span style={{ color: textFaint }}> {t('coverageModal.codesCount', { matched: matchedCount, total: codeRows.length })}</span>
          </div>
          <div className="w-px h-4" style={{ backgroundColor: divider }} />
          <div>
            <span style={{ color: textMuted }}>{t('coverageModal.hospitalTotal')}</span>{' '}
            <span className="font-bold" style={{ color: text }}>{fmtPrice(hospitalTotalPrice ?? (totalHospital || null))}</span>
          </div>
          <div className="w-px h-4" style={{ backgroundColor: divider }} />
          <div>
            <span style={{ color: textMuted }}>{t('coverageModal.nationalAvg')}</span>{' '}
            <span className="font-bold" style={{ color: '#60a5fa' }}>{fmtPrice(nationalTotalPrice ?? (totalNational || null))}</span>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto" style={{ maxHeight: 'calc(85vh - 200px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--civic-gold)' }} />
              <span className="ml-2 text-sm" style={{ color: textMuted }}>{t('coverageModal.loadingPrices')}</span>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr style={{ borderBottom: `1px solid ${divider}` }}>
                  <th className="text-left py-1.5 px-2 font-medium" style={{ color: textMuted }}>{t('coverageModal.tableCode')}</th>
                  <th className="text-left py-1.5 px-2 font-medium" style={{ color: textMuted }}>{t('coverageModal.tableProcedure')}</th>
                  <th className="text-center py-1.5 px-1 font-medium" style={{ color: textMuted }}>{t('coverageModal.tableQty')}</th>
                  <th className="text-right py-1.5 px-2 font-medium" style={{ color: textMuted }}>{t('coverageModal.tableHospital')}</th>
                  <th className="text-right py-1.5 px-2 font-medium" style={{ color: textMuted }}>{t('coverageModal.tableNationalAvg')}</th>
                  <th className="text-right py-1.5 px-2 font-medium" style={{ color: textMuted }}>{t('coverageModal.tableMarkup')}</th>
                </tr>
              </thead>
              <tbody>
                {codeRows.map((row, i) => {
                  const route = CODE_TYPE_ROUTE[row.codeType];
                  const badgeColor = CODE_TYPE_COLORS[row.codeType] || '#6b7280';
                  const markupColor = row.markup == null ? textFaint
                    : row.markup > 50 ? '#f87171'
                    : row.markup > 20 ? '#fb923c'
                    : row.markup > 0 ? '#fbbf24'
                    : row.markup > -10 ? '#4ade80'
                    : '#34d399';

                  return (
                    <tr
                      key={`${row.code}-${i}`}
                      style={{
                        borderBottom: `1px solid ${rowBorder}`,
                        opacity: row.hospitalPrice == null ? 0.5 : 1,
                      }}
                    >
                      <td className="py-1.5 px-2">
                        {route ? (
                          <Link
                            to={`${route}/${row.code}`}
                            className="inline-block font-mono text-[10px] px-1.5 py-0.5 rounded hover:opacity-80"
                            style={{ backgroundColor: `${badgeColor}22`, color: badgeColor, border: `1px solid ${badgeColor}44` }}
                          >
                            {row.code}
                          </Link>
                        ) : (
                          <span className="font-mono text-[10px] px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: `${badgeColor}22`, color: badgeColor, border: `1px solid ${badgeColor}44` }}>
                            {row.code}
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 px-2 max-w-[200px] truncate" style={{ color: text }}>
                        {row.description}
                      </td>
                      <td className="py-1.5 px-1 text-center font-mono" style={{ color: textMuted }}>
                        {row.quantity > 1 ? `×${row.quantity}` : ''}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono font-bold" style={{ color: text }}>
                        {row.hospitalPrice != null ? fmtPrice(row.hospitalPrice) : (
                          <span style={{ color: textFaint }}>—</span>
                        )}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono" style={{ color: '#60a5fa' }}>
                        {fmtPrice(row.nationalAvg)}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono font-bold" style={{ color: markupColor }}>
                        {row.markup != null ? (
                          `${row.markup > 0 ? '+' : ''}${row.markup.toFixed(0)}%`
                        ) : (
                          <span style={{ color: textFaint }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer legend */}
        {!loading && codeRows.length > 0 && (
          <div className="text-[10px] pt-2 flex items-center gap-3" style={{
            color: textFaint,
            borderTop: `1px solid ${rowBorder}`,
          }}>
            <span>{t('coverageModal.noPriceData')}</span>
            <span>{t('coverageModal.markupFormula')}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
